export interface SkillSuggestionSource {
  id?: string
  name: string
}

/** Merge global catalog + profile claimed skills into unique suggestions (canonical casing: catalog wins). */
export function buildRelatedSkillSuggestions(
  catalog: SkillSuggestionSource[],
  profileSkills?: string[] | undefined
): { name: string }[] {
  const map = new Map<string, string>()
  for (const row of catalog) {
    const t = row.name.trim()
    if (!t) continue
    map.set(t.toLowerCase(), t)
  }
  for (const s of profileSkills ?? []) {
    const t = (s ?? '').trim()
    if (!t) continue
    const k = t.toLowerCase()
    if (!map.has(k)) map.set(k, t)
  }
  return [...map.entries()]
    .sort((a, b) => a[1].localeCompare(b[1], undefined, { sensitivity: 'base' }))
    .map(([, canon]) => ({ name: canon }))
}

/**
 * Shared commit rule for skill comboboxes: an exact (case-insensitive) match
 * resolves to its canonical casing, a lone remaining suggestion wins, and
 * anything else passes through as a new skill name.
 */
export function resolveSkillNameFromSuggestions(
  trimmedQuery: string,
  suggestions: { name: string }[]
): string {
  const q = trimmedQuery.toLowerCase()
  const exact = suggestions.find((item) => item.name.trim().toLowerCase() === q)
  if (exact) return exact.name
  if (suggestions.length === 1) return suggestions[0].name
  return trimmedQuery
}

export type AppendSkillNameResult =
  | { ok: true; skills: string[] }
  | { ok: false; error: string | null }

/** Trim + case-insensitive duplicate check for plain skill-name lists (experience/education/cert drafts, onboarding). */
export function appendSkillName(skills: string[], rawName: string): AppendSkillNameResult {
  const trimmed = rawName.trim()
  if (!trimmed) return { ok: false, error: null }
  const lower = trimmed.toLowerCase()
  if (skills.some((skill) => skill.trim().toLowerCase() === lower)) {
    return { ok: false, error: `"${trimmed}" is already listed.` }
  }
  return { ok: true, skills: [...skills, trimmed] }
}

/** Stable key for comparing skill-name lists in form dirty checks. */
export function sortedSkillNamesKey(skills: string[]): string {
  return JSON.stringify([...skills].map((s) => s.trim().toLowerCase()).sort())
}
